import {NgModule} from '@angular/core';
import {CommonModule, registerLocaleData} from '@angular/common';
import {RouterModule} from '@angular/router';
import {HttpClientModule} from '@angular/common/http';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {FlexLayoutModule} from '@angular/flex-layout';
import {TranslateModule} from '@ngx-translate/core';

import localePt from '@angular/common/locales/pt';
import localeIt from '@angular/common/locales/it';
import localeZh from '@angular/common/locales/zh';
import localeFr from '@angular/common/locales/fr';
import localeEs from '@angular/common/locales/es';
import localeEn from '@angular/common/locales/en';
import localeDe from '@angular/common/locales/de';

import {CommonRestModule} from '@sharedrest/rest.module';
import {NeqConfirmationPopinComponent} from '@sharedcommon/components/confirmation-popin/confirmation-popin.component';
import {NeqInformationPopinComponent} from '@sharedcommon/components/information-popin/information-popin.component';
import {NeqGroupComponent} from './components/group/group.component';
import {NeqIconComponent} from './components/icon/icon.component';
import {NeqRgpdPopinComponent} from '@sharedcommon/components/rgpd-popin/rgpd-popin.component';
import {NeqSelectAutocompleteComponent} from './components/select-autocomplete/neq-select-autocomplete.component';
import {NeqTableComponent} from './components/table/neq-table.component';
import {NgSelectModule} from '@ng-select/ng-select';
import {AdminLoggerService, UserLoggerService} from './services/logger.service';
import {UserService} from '@sharedcommon/services/user.service';
import {UserStore} from './store/user.store';
import {ToastService} from './services/toast.service';
import {VarDirective} from './directives/ng-var.directive';
import {LocalDateFormatPipe} from '@sharedcommon/pipe/localdate.pipe';
import {LocalTimeFormatPipe} from '@sharedcommon/pipe/localtime.pipe';
import {LocalDatetimeFormatPipe} from '@sharedcommon/pipe/localdatetime.pipe';
import {HeaderService} from './services/header.service';
import {MinValueValidatorDirective} from './directives/min-value-validator.directive';
import {MaxValueValidatorDirective} from './directives/max-value-validator.directive';
import {NeqGaugeComponent} from './components/gauge/gauge.component';
import {NeqValidationComponent} from './components/validation/validation.component';
import {HasRightsDirective} from './directives/has-rights.directive';
import {NullValueDirective} from './directives/null-value.directive';
import {NeqSelectComponent} from './components/select/neq-select.component';
import {ClassVariableTranslateServiceClient, VariableTranslateServiceClient} from './rest/equation.restservice';
import {TranslationServiceClient} from '@sharedrest/technical.restservice';
import {NeqGoogleAnalyticsService} from '@sharedcommon/services/neq-google-analytics.service';
import {NeqTreeTreeComponent} from './components/tree/neq-tree.component';
import {NeqErrorPopinComponent} from '@sharedcommon/components/error-popin/error-popin.component';
import {NeqDialogErrorComponent} from '@sharedcommon/components/dialog/dialog-error.component';
import {NeqDialogService} from '@sharedcommon/services/dialog.service';
import {TechnicalDocService} from '@sharedcommon/services/technical-doc.service';
import {InputSelectAllDirective} from '@sharedcommon/directives/input-select-all.directive';
import {PrimengTableComponent} from '@sharedcommon/components/primeng-table/primeng-table.component';
import {NutrimentValueDirective} from '@usercommon/components/rawmaterials-or-samples/rawmaterials-or-samples-values/rawmaterials-or-samples-values.component';
import {FormErrorsMessagesComponent} from '@sharedcommon/components/form-errors-messages/form-errors-messages.component';
import {RedirectGuard} from '@sharedcommon/guards/redirect.guard';
import {
  PrimengTableCellTypesComponent
} from '@sharedcommon/components/primeng-table/primeng-table-cell-types/primeng-table-cell-types.component';
import {NeqEquationEditorComponent} from '@admincommon/components/equation-editor/equation-editor.component';
import {
  PrimengTableCellInputsVariableComponent
} from '@sharedcommon/components/primeng-table/primeng-table-cell-inputs-variable/primeng-table-cell-inputs-variable.component';
import {NeqConditionEditorComponent} from '@admincommon/components/condition-editor/condition-editor.component';
import {NeqVariableValueComponent} from '@admincommon/components/variable-value/variable-value.component';
import {NeqLocalStorageService} from '@sharedcommon/services/neq-local-storage.service';
import {NeqPaginationService} from '@sharedcommon/services/neq-pagination.service';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {BrowserModule} from '@angular/platform-browser';
import {MatDialogModule} from '@angular/material/dialog';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatMenuModule} from '@angular/material/menu';
import {MatIconModule} from '@angular/material/icon';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatRadioModule} from '@angular/material/radio';
import {MatSelectModule} from '@angular/material/select';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatTabsModule} from '@angular/material/tabs';
import {MatListModule} from '@angular/material/list';
import {MatButtonModule} from '@angular/material/button';
import {MatTreeModule} from '@angular/material/tree';
import {TableModule} from 'primeng/table';
import {InputTextModule} from 'primeng/inputtext';
import {InputTextareaModule} from 'primeng/inputtextarea'
import {FileUploadModule} from 'primeng/fileupload';
import {CalendarModule} from 'primeng/calendar';
import {CheckboxModule} from 'primeng/checkbox';
import {ButtonModule} from 'primeng/button';
import {TreeModule} from 'primeng/tree';
import {NeqEventListComponent} from '@sharedcommon/components/event/event-list.component';
import {NeqRawmaterialsValuesDialogComponent} from '@usercommon/components/rawmaterials/dialog/rawmaterials-values.dialog';
import {
  RawmaterialsExportDialogComponent
} from '@usercommon/components/rawmaterials/rawmaterials-export-dialog/rawmaterials-export-dialog.component';
import {VariableValuePredictionModelValueDirective} from '@sharedcommon/directives/variable-value-prediction-model-value.directive';
import {VariableValueVariableValueDirective} from '@sharedcommon/directives/variable-value-variable-value.directive';
import {NgxGoogleAnalyticsModule} from 'ngx-google-analytics';
import {environment} from '@sharedenvironment';
import {TreeSelectModule} from 'primeng/treeselect';
import {
  CalculationStatusIconComponent
} from '@usercommon/components/rawmaterials/calculation-status-icon/calculation-status-icon.component';
import {
  PrimengTableSelectFilterComponent
} from '@sharedcommon/components/primeng-table/primeng-table-select-filter/primeng-table-select-filter.component';
import {DropdownModule} from 'primeng/dropdown';
import { PrimengTableDateFilterComponent } from './components/primeng-table/primeng-table-date-filter/primeng-table-date-filter.component';
import { FileDragNDropDirective } from './directives/file-drag-n-drop.directive';
import { PrimengTableCellInputsRawmaterialComponent } from './components/primeng-table/primeng-table-cell-inputs-rawmaterial/primeng-table-cell-inputs-rawmaterial.component';
import { HasMenuRightsDirective } from './directives/has-menu-rights.directive';
import { NeqEditionPopinComponent } from './components/edition-popin/edition-popin.component';
import { NeqRawmaterialsMultiEditSizeLimitDialogComponent } from '@usercommon/components/rawmaterials/rawmaterials-multi-edit-size-limit-popin/rawmaterials-multi-edit-size-limit.dialog';

registerLocaleData(localeFr, 'fr');
registerLocaleData(localeEn, 'en');
registerLocaleData(localeDe, 'de');
registerLocaleData(localeIt, 'it');
registerLocaleData(localeEs, 'es');
registerLocaleData(localePt, 'pt');
registerLocaleData(localeZh, 'zh');

@NgModule({
  declarations: [
    NeqEventListComponent,
    NeqIconComponent,
    NeqTableComponent,
    NeqValidationComponent,
    NeqEquationEditorComponent,
    NeqConditionEditorComponent,
    NeqVariableValueComponent,
    VariableValuePredictionModelValueDirective,
    VariableValueVariableValueDirective,
    PrimengTableCellInputsVariableComponent,
    PrimengTableCellInputsRawmaterialComponent,
    NeqSelectAutocompleteComponent,
    NeqGroupComponent,
    VarDirective,
    LocalDateFormatPipe,
    LocalTimeFormatPipe, LocalDatetimeFormatPipe, MinValueValidatorDirective, MaxValueValidatorDirective,
    NeqGaugeComponent, HasRightsDirective, HasMenuRightsDirective, NullValueDirective, NeqDialogErrorComponent, InputSelectAllDirective,
    NeqEditionPopinComponent,
    NeqConfirmationPopinComponent,NeqRawmaterialsValuesDialogComponent, NeqRawmaterialsMultiEditSizeLimitDialogComponent, NeqInformationPopinComponent, NeqErrorPopinComponent, NeqRgpdPopinComponent, NeqSelectComponent, NeqTreeTreeComponent,
    PrimengTableComponent, NutrimentValueDirective, FormErrorsMessagesComponent, PrimengTableCellTypesComponent,
    PrimengTableSelectFilterComponent, PrimengTableDateFilterComponent,
    RawmaterialsExportDialogComponent, CalculationStatusIconComponent, FileDragNDropDirective
  ],
  imports: [
    BrowserModule,
    CommonModule, CommonRestModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatDialogModule,
    TableModule,
    InputTextareaModule,
    FileUploadModule,
    CalendarModule,
    CheckboxModule,
    InputTextModule,
    ButtonModule,
    RouterModule,
    MatToolbarModule, MatMenuModule, MatIconModule, MatFormFieldModule, MatInputModule, MatRadioModule,
    MatSelectModule, MatSnackBarModule, MatCheckboxModule, MatTooltipModule, MatAutocompleteModule, MatProgressSpinnerModule,
    MatTabsModule, MatListModule, MatButtonModule,
    BrowserAnimationsModule,
    FlexLayoutModule,
    HttpClientModule,
    DropdownModule,
    NgSelectModule, MatTreeModule, TreeModule, TreeSelectModule,
    NgxGoogleAnalyticsModule.forRoot(environment.GOOGLE_ANALYTICS_TRACKING_ID)
  ],
  exports: [
    BrowserModule,
    CommonModule, CommonRestModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatDialogModule,
    TableModule,
    InputTextareaModule,
    FileUploadModule,
    CalendarModule,
    CheckboxModule,
    InputTextModule,
    ButtonModule,
    RouterModule,
    MatToolbarModule, MatMenuModule, MatIconModule, MatFormFieldModule, MatInputModule, MatRadioModule,
    MatSelectModule, MatSnackBarModule, MatCheckboxModule, MatTooltipModule, MatAutocompleteModule, MatProgressSpinnerModule,
    MatTabsModule, MatListModule, MatButtonModule,
    BrowserAnimationsModule,
    FlexLayoutModule,
    HttpClientModule,
    NgSelectModule, MatTreeModule, TreeModule, TreeSelectModule,
    NeqEventListComponent,
    NeqIconComponent, NeqTableComponent, NeqValidationComponent, NeqEquationEditorComponent,
    NeqConditionEditorComponent, NeqVariableValueComponent, VariableValuePredictionModelValueDirective,
    VariableValueVariableValueDirective, NeqRawmaterialsValuesDialogComponent, NeqRawmaterialsMultiEditSizeLimitDialogComponent,
    PrimengTableCellInputsVariableComponent,
    NeqSelectAutocompleteComponent, NeqGroupComponent, VarDirective, LocalDateFormatPipe,
    LocalTimeFormatPipe, LocalDatetimeFormatPipe, MinValueValidatorDirective, MaxValueValidatorDirective,
    NeqGaugeComponent, HasRightsDirective, HasMenuRightsDirective, NullValueDirective, NeqDialogErrorComponent, InputSelectAllDirective,
    NeqEditionPopinComponent,
    NeqConfirmationPopinComponent, NeqInformationPopinComponent, NeqErrorPopinComponent, NeqRgpdPopinComponent, NeqSelectComponent, NeqTreeTreeComponent,
    PrimengTableComponent, NutrimentValueDirective, FormErrorsMessagesComponent, PrimengTableCellTypesComponent,
    PrimengTableSelectFilterComponent, PrimengTableDateFilterComponent,
    RawmaterialsExportDialogComponent, CalculationStatusIconComponent, FileDragNDropDirective
  ],
  providers: [
    UserStore,
    UserService,
    TechnicalDocService,
    NeqLocalStorageService,
    NeqPaginationService,
    UserLoggerService,
    AdminLoggerService,
    HeaderService,
    ToastService,
    NeqDialogService,
    VariableTranslateServiceClient,
    ClassVariableTranslateServiceClient,
    TranslationServiceClient,
    NeqGoogleAnalyticsService,
    RedirectGuard
  ]
})
export class SharedCommonModule {
}
